/**
 * Quitter une conversation pendant qu'elle rédige (#859).
 *
 * Deux garde-fous vivent autour de cette règle, et il faut les distinguer :
 * - **l'avertissement natif du navigateur** à la fermeture de l'onglet, qui est
 *   la seule chose capable d'empêcher une perte réelle ;
 * - **le message de ce module**, qui n'empêche rien et n'a qu'un but
 *   pédagogique : apprendre à l'avocat que la rédaction continue là-bas et que
 *   fermer l'onglet la perdrait.
 *
 * La règle de décision est isolée ici parce qu'elle est faite de conditions
 * dont chacune, oubliée, produit une nuisance ou un silence — et qu'aucune ne
 * se voit dans un composant.
 */

/** Ce que l'affichage doit savoir pour décider, sans rien connaître de React. */
export interface DepartObserve {
  /** Le fil que l'avocat regardait juste avant. `null` = il n'en regardait aucun. */
  precedente: string | null;
  /** Le fil qu'il regarde maintenant. `null` = il a quitté pour un autre écran. */
  courante: string | null;
  /** Les conversations qui rédigent à cet instant. */
  quiRedigent: readonly string[];
  /** Le message pédagogique a-t-il déjà été montré dans cet onglet ? */
  dejaAverti: boolean;
}

/**
 * Faut-il avertir, et à propos de quelle conversation ?
 *
 * Quatre conditions, dans cet ordre :
 *
 * 1. **Il y a bien un départ.** Sans fil précédent, il n'y a rien à quitter —
 *    c'est le cas au tout premier montage, où avertir reviendrait à parler
 *    d'une navigation qui n'a pas eu lieu.
 * 2. **Le fil a réellement changé.** Un simple re-rendu ne quitte rien.
 * 3. **Le fil quitté rédigeait au moment du départ.** C'est le seul fait qui
 *    justifie le message ; une rédaction déjà terminée n'a plus rien à perdre.
 * 4. **On ne l'a pas déjà dit.** Le message enseigne une propriété du produit,
 *    pas un événement : la répéter à chaque aller-retour en ferait du bruit, et
 *    c'est le bandeau permanent qui prend le relais ensuite.
 *
 * Noter ce qui n'est **pas** une condition : la destination. Aller sur un autre
 * fil, sur un dossier ou sur l'accueil, c'est le même départ — dans les trois
 * cas la rédaction se poursuit hors de vue.
 */
export function deciderAvertissementDeDepart({
  precedente,
  courante,
  quiRedigent,
  dejaAverti,
}: DepartObserve): string | null {
  if (dejaAverti) return null;
  if (!precedente || precedente === courante) return null;
  if (!quiRedigent.includes(precedente)) return null;
  return precedente;
}

/**
 * Le message montré à l'avocat.
 *
 * **Le mot « onglet » doit y figurer** : c'est le seul moment du produit où il
 * apparaît, et c'est la seule information qui protège d'une perte — le texte
 * d'une rédaction ne passe par aucun tampon serveur, donc fermer l'onglet le
 * perd sans recours (#857).
 *
 * Le nom du fil est optionnel parce que la liste des conversations peut ne pas
 * être encore chargée : on préfère un message sans nom à un nom faux, même
 * règle que la pastille du porteur (`canvasHolder.ts`).
 */
export function messageDeDepart(nomDeLaConversation: string | null): string {
  const ou = nomDeLaConversation
    ? `dans « ${nomDeLaConversation} »`
    : 'dans la conversation que vous venez de quitter';
  return `La rédaction continue ${ou}. Ne fermez pas cet onglet : le texte en cours d’écriture serait perdu.`;
}
